import React, { useState } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { authService } from '../../services/authService';

const AuthContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
`;

const AuthCard = styled.div`
  background: white;
  padding: 40px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  width: 100%;
  max-width: 400px;
`;

const Logo = styled(Link)`
  font-size: 24px;
  font-weight: bold;
  color: #000;
  text-decoration: none;
  display: block;
  text-align: center;
  margin-bottom: 32px;
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 600;
  margin-bottom: 12px;
  text-align: center;
`;

const Subtitle = styled.p`
  font-size: 14px;
  color: #666;
  text-align: center;
  margin-bottom: 24px;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-size: 14px;
  color: #666;
`;

const Input = styled.input`
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 16px;

  &:focus {
    outline: none;
    border-color: #000;
  }
`;

const Button = styled.button`
  background: #000;
  color: white;
  padding: 12px;
  border: none;
  border-radius: 4px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  margin-top: 8px;

  &:hover {
    opacity: 0.9;
  }
`;

const BackToLogin = styled(Link)`
  color: #666;
  text-align: center;
  font-size: 14px;
  margin-top: 16px;
  display: block;
  text-decoration: none;

  &:hover {
    color: #000;
  }
`;

const StatusMessage = styled.div`
  text-align: center;
  margin-bottom: 24px;
  padding: 12px;
  border-radius: 4px;
  background: ${props => props.$success ? '#ecfdf5' : '#fef2f2'};
  color: ${props => props.$success ? '#059669' : '#dc2626'};
`;

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setStatus('');
    setIsLoading(true);

    try {
      await authService.forgotPassword(email);
      setIsSubmitted(true);
      setStatus('Reset instructions have been sent to your email address. Please check your inbox and spam folder.');
    } catch (err) {
      console.error('Password reset error:', err);
      setError(err.message || 'Failed to send reset email. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthContainer>
      <AuthCard>
        <Logo to="/">nexpend.</Logo>
        <Title>Reset Password</Title>
        <Subtitle>
          Enter your email address and we'll send you a link to reset your password.
        </Subtitle>
        
        {error && <StatusMessage>{error}</StatusMessage>}
        {status && <StatusMessage $success>{status}</StatusMessage>}
        
        {!isSubmitted ? (
          <Form onSubmit={handleSubmit}>
            <FormGroup>
              <Label>Email</Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="Enter your email"
                disabled={isLoading}
              />
            </FormGroup>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? 'Sending...' : 'Send Reset Link'}
            </Button>
          </Form>
        ) : (
          <>
            <StatusMessage $success>
              <p style={{ marginBottom: '8px' }}>✓ Reset link sent!</p>
              <p style={{ fontSize: '14px' }}>
                Check your email (including spam folder) for instructions to reset your password.
              </p>
            </StatusMessage>
            <Button onClick={() => {
              setIsSubmitted(false);
              setEmail('');
              setStatus('');
            }}>
              Try another email
            </Button>
          </>
        )}
        
        <BackToLogin to="/login">
          ← Back to Login
        </BackToLogin>
      </AuthCard>
    </AuthContainer>
  );
};

export default ForgotPassword; 